import { useCallback } from 'react';
import { useMarkBlankMutation, useMarkBreachMutation, useClearUserFlagsMutation, Page } from '@/api';
import { useAppDispatch } from '@/store';
import { updatePagesLocally } from '@/store/slices/gdpr/documents';
import { filterPagesByAction } from '../utils';

interface UseGdprActionsProps {
  documentId?: string;
  hasSelection: boolean;
  selectedPageIds: string[];
  pages: Page[] | null;
  selectedIndex: number;
  clearSelection: () => void;
  refetchHistory?: () => void; // Add history refetch callback
}

export const useGdprActions = ({
  documentId,
  hasSelection,
  selectedPageIds,
  pages,
  selectedIndex,
  clearSelection,
  refetchHistory,
}: UseGdprActionsProps) => {
  // Redux dispatch for local updates
  const dispatch = useAppDispatch();

  // API mutations
  const [markBlank, { isLoading: isMarkingBlank }] = useMarkBlankMutation();
  const [markBreach, { isLoading: isMarkingBreach }] = useMarkBreachMutation();
  const [clearUserFlags, { isLoading: isClearingFlags }] = useClearUserFlagsMutation();

  const handleMarkBreach = useCallback(async () => {
    const allPageIds = hasSelection
      ? selectedPageIds
      : [pages?.[selectedIndex]?.id].filter((id): id is string => Boolean(id));
    const filteredPageIds = filterPagesByAction(allPageIds, pages, 'breach');
    if (filteredPageIds.length === 0) {
      return;
    }
    try {
      const updatedPages = await markBreach({
        pageIds: filteredPageIds,
        documentId,
      }).unwrap();

      // Update local state with the response data (array of PageUpdate objects)
      if (updatedPages.length > 0) {
        dispatch(updatePagesLocally(updatedPages));
      }

      // Refetch history for undo/redo button states
      if (refetchHistory) {
        refetchHistory();
      }

      if (hasSelection) clearSelection();
    } catch (error) {
      console.error('Failed to mark pages as breach:', error);
    }
  }, [
    markBreach,
    hasSelection,
    selectedPageIds,
    pages,
    selectedIndex,
    clearSelection,
    documentId,
    dispatch,
    refetchHistory,
  ]);

  const handleMarkBlank = useCallback(async () => {
    const allPageIds = hasSelection
      ? selectedPageIds
      : [pages?.[selectedIndex]?.id].filter((id): id is string => Boolean(id));
    const filteredPageIds = filterPagesByAction(allPageIds, pages, 'blank');
    if (filteredPageIds.length === 0) {
      return;
    }
    try {
      const updatedPages = await markBlank({
        pageIds: filteredPageIds,
        documentId,
      }).unwrap();

      // Update local state with the response data (array of PageUpdate objects)
      if (updatedPages.length > 0) {
        dispatch(updatePagesLocally(updatedPages));
      }

      // Refetch history for undo/redo button states
      if (refetchHistory) {
        refetchHistory();
      }

      if (hasSelection) clearSelection();
    } catch (error) {
      console.error('Failed to mark pages as blank:', error);
    }
  }, [
    markBlank,
    hasSelection,
    selectedPageIds,
    pages,
    selectedIndex,
    clearSelection,
    documentId,
    dispatch,
    refetchHistory,
  ]);

  const handleClearLabels = useCallback(async () => {
    const allPageIds = hasSelection
      ? selectedPageIds
      : [pages?.[selectedIndex]?.id].filter((id): id is string => Boolean(id));
    const filteredPageIds = filterPagesByAction(allPageIds, pages, 'clearLabels');
    if (filteredPageIds.length === 0) {
      return;
    }
    try {
      const updatedPages = await clearUserFlags({
        pageIds: filteredPageIds,
        documentId,
      }).unwrap();

      // Update local state with the response data (array of PageUpdate objects)
      if (updatedPages.length > 0) {
        dispatch(updatePagesLocally(updatedPages));
      }

      // Refetch history for undo/redo button states
      if (refetchHistory) {
        refetchHistory();
      }

      if (hasSelection) clearSelection();
    } catch (error) {
      console.error('Failed to clear labels:', error);
    }
  }, [
    clearUserFlags,
    hasSelection,
    selectedPageIds,
    pages,
    selectedIndex,
    clearSelection,
    documentId,
    dispatch,
    refetchHistory,
  ]);

  return {
    handleMarkBreach,
    handleMarkBlank,
    handleClearLabels,
    isMarkingBreach,
    isMarkingBlank,
    isClearingFlags,
    isLoading: isMarkingBreach || isMarkingBlank || isClearingFlags,
  };
};
